import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import Button from "./common/Button";
import { ArrowLeft, Save, Eye, X } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CustomConsentEditor.css';
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";

function localizeQuillToolbar(toolbarEl, t) {
  if (!toolbarEl) return;

  const buttonLabels = [
    [".ql-bold", "app.consent.quill.bold", "Negrita"],
    [".ql-italic", "app.consent.quill.italic", "Cursiva"],
    [".ql-underline", "app.consent.quill.underline", "Subrayado"],
    [".ql-strike", "app.consent.quill.strike", "Tachado"],
    ['.ql-list[value="ordered"]', "app.consent.quill.orderedList", "Lista numerada"],
    ['.ql-list[value="bullet"]', "app.consent.quill.bulletList", "Lista con viñetas"],
    ['.ql-indent[value="-1"]', "app.consent.quill.indentLess", "Disminuir sangría"],
    ['.ql-indent[value="+1"]', "app.consent.quill.indentMore", "Aumentar sangría"],
    [".ql-link", "app.consent.quill.link", "Enlace"],
    [".ql-image", "app.consent.quill.image", "Imagen"],
    [".ql-clean", "app.consent.quill.clean", "Limpiar formato"],
  ];

  buttonLabels.forEach(([selector, key, fallback]) => {
    const el = toolbarEl.querySelector(selector);
    if (el) el.setAttribute("title", t(key, fallback));
  });

  const headerPicker = toolbarEl.querySelector(".ql-header");
  if (headerPicker) {
    const headerLabel = headerPicker.querySelector(".ql-picker-label");
    if (headerLabel) {
      headerLabel.setAttribute("title", t("app.consent.quill.header", "Estilo de texto"));
    }

    const headerLabels = {
      "": t("app.consent.quill.headerNormal", "Normal"),
      1: t("app.consent.quill.headerH1", "Título 1"),
      2: t("app.consent.quill.headerH2", "Título 2"),
      3: t("app.consent.quill.headerH3", "Título 3"),
      4: t("app.consent.quill.headerH4", "Título 4"),
      5: t("app.consent.quill.headerH5", "Título 5"),
      6: t("app.consent.quill.headerH6", "Título 6"),
    };

    headerPicker.querySelectorAll(".ql-picker-item").forEach((item) => {
      const value = item.getAttribute("data-value") ?? "";
      if (headerLabels[value] !== undefined) {
        item.textContent = headerLabels[value];
      }
    });
  }

  const alignPicker = toolbarEl.querySelector(".ql-align");
  if (alignPicker) {
    const alignLabel = alignPicker.querySelector(".ql-picker-label");
    if (alignLabel) {
      alignLabel.setAttribute("title", t("app.consent.quill.align", "Alineación"));
    }

    const alignLabels = {
      "": t("app.consent.quill.alignLeft", "Izquierda"),
      center: t("app.consent.quill.alignCenter", "Centro"),
      right: t("app.consent.quill.alignRight", "Derecha"),
      justify: t("app.consent.quill.alignJustify", "Justificado"),
    };

    alignPicker.querySelectorAll(".ql-picker-item").forEach((item) => {
      const value = item.getAttribute("data-value") ?? "";
      if (alignLabels[value] !== undefined) {
        item.textContent = alignLabels[value];
      }
    });
  }
}

export default function CustomConsentEditor() {
  const { t, language } = useBrandTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supabaseUserId, setSupabaseUserId] = useState(null);
  const quillRef = useRef(null);

  // Obtener el ID de Supabase Auth si existe una sesión
  useEffect(() => {
    const getSupabaseUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          setSupabaseUserId(session.user.id);
        } else {
          // Si no hay sesión de Supabase Auth, usar el ID de profiles
          setSupabaseUserId(user?.id || null);
        }
      } catch (error) {
        console.error("Error getting Supabase session:", error);
        // Fallback al ID de profiles
        setSupabaseUserId(user?.id || null);
      }
    };

    if (user) {
      getSupabaseUser();
    }
  }, [user]);

  // Handler para subir imágenes (memoizado para evitar recreaciones)
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        customToast(t("app.messaging.imageTooLarge", "La imagen es demasiado grande. Máximo 5MB."), "danger");
        return;
      }

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/utils/upload', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(
            error.error ||
              t("app.consent.quill.uploadImageError", "Error al subir la imagen")
          );
        }

        const result = await response.json();
        const imageUrl = result.url;

        // Obtener la instancia de Quill
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          const index = range ? range.index : quill.getLength();
          // Insertar la imagen en el editor
          quill.insertEmbed(index, 'image', imageUrl, 'user');
          // Mover el cursor después de la imagen
          quill.setSelection(index + 1);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        customToast(t("app.messaging.uploadError", "Error al subir la imagen: {{message}}", { message: error.message }), "danger");
      } finally {
        setLoading(false);
      }
    };
  }, [t]);

  // Configuración del editor Quill (memoizada para evitar recreaciones)
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false,
    },
  }), [imageHandler]);

  const quillFormats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'align', 'link', 'image'
  ], []);

  useEffect(() => {
    if (isPreview) return undefined;

    const timer = window.setTimeout(() => {
      const editor = quillRef.current?.getEditor?.();
      const toolbarEl = editor?.container?.previousElementSibling;
      localizeQuillToolbar(toolbarEl, t);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [t, language, isPreview, loading]);
  // Cargar documento existente si estamos editando
  useEffect(() => {
    if (id && user) {
      loadDocument(parseInt(id));
    }
  }, [id, user, navigate]);

  const loadDocument = async (documentId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("custom_consent_documents")
        .select("*")
        .eq("id", documentId)
        .eq("created_by", user.id)
        .eq("is_active", true)
        .single();

      if (error) {
        console.error("Error loading document:", error);
        customToast(t("app.consent.loadError", "Error al cargar el documento"), "danger");
        navigate("/informed-consent");
        return;
      }

      if (data) {
        setTitle(data.title);
        setContent(data.content);
        setIsEditing(true);
      } else {
        customToast(t("app.consent.docNotFound", "Documento no encontrado"), "danger");
        navigate("/informed-consent");
      }
    } catch (error) {
      console.error("Error loading document:", error);
      customToast(t("app.consent.loadError", "Error al cargar el documento"), "danger");
      navigate("/informed-consent");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Limpiar el contenido HTML para verificar si tiene texto real
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    
    if (!title.trim() || !textContent) {
      customToast(t("app.consent.titleContentRequired", "Por favor completa el título y contenido del documento"), "warning");
      return;
    }

    if (!user) {
      customToast(t("app.consent.authRequired", "Debes estar autenticado para guardar documentos"), "danger");
      return;
    }

    try {
      setLoading(true);
      // Usar el ID de Supabase Auth si está disponible, sino el de profiles
      const userIdToUse = supabaseUserId || user?.id;
      
      if (!userIdToUse) {
        customToast(t("app.consent.userNotFound", "Error: No se pudo identificar al usuario. Inicia sesión nuevamente."), "danger");
        return;
      }

      const documentData = {
        title: title.trim(),
        content: content.trim(),
        category: "Custom",
        size: `${(content.length / 1024).toFixed(1)} Kb`,
        created_by: userIdToUse,
        description: t(
          "app.consent.customDocSubtitle",
          "Documento de consentimiento personalizado"
        ),
      };

      if (isEditing) {
        // Actualizar documento existente
        const { error } = await supabase
          .from("custom_consent_documents")
          .update({
            ...documentData,
            updated_at: new Date().toISOString()
          })
          .eq("id", parseInt(id))
          .eq("created_by", user.id);

        if (error) {
          console.error("Error updating document:", error);
          customToast(t("app.consent.updateError", "Error al actualizar el documento"), "danger");
          return;
        }
        customToast(t("app.consent.updateSuccess", "Documento actualizado exitosamente"), "success");
      } else {
        // Crear nuevo documento
        const { error } = await supabase
          .from("custom_consent_documents")
          .insert([documentData]);

        if (error) {
          console.error("Error creating document:", error);
          if (error.code === '23503') {
            customToast(t("app.consent.userNotInSystem", "Error: El usuario no existe en el sistema. Contacta al administrador."), "danger");
          } else {
            customToast(t("app.consent.createError", "Error al crear el documento: {{message}}", { message: error.message || t("app.commonUi.error", "Error") }), "danger");
          }
          return;
        }
        customToast(t("app.consent.saveSuccess", "Documento guardado exitosamente"), "success");
      }
      
      navigate("/informed-consent");
    } catch (error) {
      console.error("Error saving document:", error);
      customToast(t("app.consent.saveError", "Error al guardar el documento"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    
    if (!title.trim() || !textContent) {
      customToast(t("app.consent.previewRequired", "Por favor completa el título y contenido antes de la vista previa"), "warning");
      return;
    }
    setIsPreview(!isPreview);
  };

  if (loading && id && !title && !content) {
    return (
      <div className="bg-gray-50 p-4 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p>{t("app.consent.loadingEditor", "Cargando editor...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto mb-20">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate("/informed-consent")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("app.consent.backToDocs", "Volver a Documentos")}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isEditing
              ? t("app.consent.editConsentTitle", "Editar Documento de Consentimiento")
              : t("app.consent.createConsentTitle", "Crear Documento de Consentimiento")}
          </h1>
          <p className="text-gray-600">
            {isEditing
              ? t("app.consent.editConsentSubtitle", "Edita el documento de consentimiento informado")
              : t(
                  "app.consent.createConsentSubtitle",
                  "Crea un documento de consentimiento informado personalizado"
                )}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("app.consent.editorTitle", "Editor de Documento")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Título del documento */}
            <div>
              <Label htmlFor="title">{t("app.consent.docTitle", "Título del Documento")}</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("app.consent.docTitlePlaceholder", "Ej: Consentimiento para Cirugía de Rodilla")}
                className="mt-2"
              />
            </div>

            {/* Contenido del documento */}
            <div>
              <Label htmlFor="content">{t("app.consent.docContent", "Contenido del Documento")}</Label>
              <div className="mt-2 min-h-[400px]">
                {isPreview ? (
                  <div className="rounded-md p-4 bg-white min-h-[400px] border border-gray-200">
                    <p className="text-sm font-medium text-gray-500 mb-4">
                      {t("app.consent.previewSection", "Vista previa del documento")}
                    </p>
                    <div className="prose max-w-none">
                      <h2 className="text-xl font-bold mb-4 text-center">{title}</h2>
                      <div className="text-center mb-6">
                        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                          {t("app.clinical.informedConsentSubtitle", "Consentimiento Informado")}
                        </span>
                      </div>
                      <div 
                        className="preview-content"
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="consent-quill rounded-md" style={{ backgroundColor: '#ffffff' }}>
                    <ReactQuill
                      key={`quill-editor-${language}`}
                      ref={quillRef}
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={t("app.consent.docContentPlaceholder", "Escribe aquí el contenido del documento de consentimiento...")}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 pt-4 flex-col">
              <Button 
                onClick={handleSave} 
                disabled={loading}
                className="flex items-center gap-2 mt-4"
              >
                <Save className="w-4 h-4" />
                {loading
                  ? t("app.consent.saving", "Guardando...")
                  : isEditing
                    ? t("app.consent.updateDocument", "Actualizar Documento")
                    : t("app.consent.saveDocument", "Guardar Documento")}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handlePreview}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                {isPreview
                  ? t("app.consent.edit", "Editar")
                  : t("app.consent.preview", "Vista Previa")}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  if (window.confirm(t("app.consent.confirmCancel", "¿Desea cancelar la operación?"))) {
                    navigate("/informed-consent");
                  }
                }}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {t("app.consent.cancel", "Cancelar")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
